import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Gift,
  X,
  Sparkles,
  CheckCircle,
  Loader2,
  Mail
} from "lucide-react";
import { toast } from "sonner";
import {
  shouldShowEmailPopup,
  markPopupShown,
  setEmailCaptured,
  trackEmailCapture,
  trackVisitorBehavior
} from "@/utils/analytics";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailCapturePopup = ({ discountAmount = 25, onClose }) => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // NEVER show on admin pages - check immediately
    if (window.location.pathname.startsWith('/admin')) {
      return;
    }
    
    // Check if we should show the popup
    const timer = setTimeout(() => {
      // Double-check admin path in case of navigation
      if (window.location.pathname.startsWith('/admin')) {
        return;
      }
      if (shouldShowEmailPopup()) {
        setShow(true);
        markPopupShown();
        trackVisitorBehavior('popup_shown', { type: 'email_capture' });
      }
    }, 40000); // Show after 40 seconds

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setShow(false);
    trackVisitorBehavior('popup_closed', { type: 'email_capture', submitted: submitted });
    if (onClose) onClose();
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      // Save to backend — passing discount_code: null tells the backend to
      // mint a UNIQUE code (SAVE25-XXXXXXXX) tied to this email. The old
      // hard-coded "WELCOME25" string is gone so codes are no longer
      // reusable/shareable across prospects.
      const response = await fetch(`${API_URL}/api/leads/email-capture`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim() || null,
          source: "popup",
          discount_code: null,
          sms_consent: smsConsent
        })
      });

      if (response.ok) {
        setEmailCaptured(email);
        trackEmailCapture('popup');
        setSubmitted(true);
        
        // Show success toast after a delay
        setTimeout(() => {
          toast.success(
            <div>
              <p className="font-semibold">Welcome! Your ${discountAmount} discount is ready!</p>
              <p className="text-sm">Check your email for your exclusive discount code.</p>
            </div>
          );
          handleClose();
        }, 2000);
      } else {
        const data = await response.json();
        setError(data.detail || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Email capture error:", err);
      setError("Unable to process your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto bg-white shadow-2xl relative overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 z-10"
          data-testid="close-email-popup"
        >
          <X className="w-5 h-5" />
        </button>

        {!submitted ? (
          <>
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Gift className="w-8 h-8" />
              </div>
              <CardTitle className="text-2xl font-bold mb-2">
                Get ${discountAmount} Off!
              </CardTitle>
              <p className="text-orange-100 text-sm">
                Your first service with OC Tasker
              </p>
            </div>

            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Benefits */}
                <div className="bg-orange-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-orange-800 font-medium mb-2">
                    Join thousands of happy homeowners:
                  </p>
                  <ul className="text-sm text-orange-700 space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Instant ${discountAmount} discount code
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Exclusive member-only deals
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Priority scheduling
                    </li>
                  </ul>
                </div>

                {/* Name (optional) */}
                <div className="space-y-2">
                  <Label htmlFor="popup-name">Name (optional)</Label>
                  <Input
                    id="popup-name"
                    placeholder="John"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="popup-name-input"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="popup-email">Email Address *</Label>
                  <Input
                    id="popup-email"
                    type="email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                    className={error ? "border-red-500" : ""}
                    data-testid="popup-email-input"
                  />
                  {error && (
                    <p className="text-sm text-red-500">{error}</p>
                  )}
                </div>

                {/* SMS Consent */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="popup-sms"
                    checked={smsConsent}
                    onCheckedChange={setSmsConsent}
                    className="mt-1"
                  />
                  <label htmlFor="popup-sms" className="text-xs text-gray-500 cursor-pointer">
                    Also send me text updates about exclusive deals and appointment reminders
                  </label>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-6 text-lg bg-orange-500 hover:bg-orange-600"
                  data-testid="popup-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Get My ${discountAmount} Discount
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-gray-400">
                  No spam, unsubscribe anytime. We respect your privacy.
                </p>
              </form>
            </CardContent>
          </>
        ) : (
          /* Success State */
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              You&apos;re In!
            </h3>
            <p className="text-gray-600 mb-4">
              Check your email for your exclusive ${discountAmount} discount code.
            </p>
            <div className="bg-orange-50 rounded-lg p-4 inline-flex items-center gap-2">
              <Mail className="w-5 h-5 text-orange-500" />
              <span className="text-orange-700 font-medium">{email}</span>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default EmailCapturePopup;
